// SEAPA UI kit (modernization phase 3, 2026-07-04). Small, boring, shared.
// Rule of thumb: pages own their layout and data display; the kit owns the
// look of buttons, cards, headers, and controls so every page drifts the
// same direction. Tokens live in theme.js (theme.tokens).
import React, { useEffect, useRef, useState } from "react";
import theme from "../theme";
import "./kit.css";

const T = theme.tokens;

// ---- Button ----------------------------------------------------------------
// variant: primary | outline | ghost | danger      size: md | sm
export function Button({ variant = "primary", size = "md", className = "", style, children, ...props }) {
  return (
    <button className={`ui-btn ui-btn--${size} ui-btn--${variant} ${className}`} style={style} {...props}>
      {children}
    </button>
  );
}

// ---- Card ------------------------------------------------------------------
// A white surface with hairline border + soft shadow. `accent` paints the
// left edge (e.g. theme.colors.gold for travel), `tint` fills the body.
export function Card({ accent, tint, title, titleRight, style, children, ...props }) {
  return (
    <div
      style={{
        background: tint || T.surface,
        border: `1px solid ${T.border}`,
        borderLeft: accent ? `5px solid ${accent}` : `1px solid ${T.border}`,
        borderRadius: T.radius.lg,
        boxShadow: T.shadowCard,
        padding: "16px 18px",
        textAlign: "left", // App.css #root centers text globally (legacy) — cards read left
        ...style,
      }}
      {...props}
    >
      {(title || titleRight) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          {title && <h3 className="ui-title" style={{ margin: 0, color: theme.colors.primary, fontSize: T.text.lg, fontWeight: 800, letterSpacing: "-0.01em" }}>{title}</h3>}
          {titleRight}
        </div>
      )}
      {children}
    </div>
  );
}

// ---- MicroLabel ------------------------------------------------------------
// The uppercase section tag ("NEXT THREE WEEKS", "LOCATION").
export function MicroLabel({ style, children }) {
  return (
    <div style={{ fontSize: T.text.xs, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: T.faint, ...style }}>
      {children}
    </div>
  );
}

// ---- PageHeader --------------------------------------------------------------
// ---- EmptyState --------------------------------------------------------------
// One voice for "nothing here yet" (design review item 21). Every empty state
// says what will appear and, when there's an action the person can take, how
// to make it happen. Quiet by design: no card, no icon, no italics.
export function EmptyState({ title, hint, action, style }) {
  return (
    <div style={{ padding: "28px 16px", textAlign: "center", ...style }}>
      <div style={{ color: T.textMuted, fontWeight: 600, fontSize: T.text.md }}>{title}</div>
      {hint && <div style={{ color: T.faint, fontSize: T.text.sm, marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

// ---- FileDrop --------------------------------------------------------------
// Styled replacement for the browser's raw file input on the office upload
// pages: click (or Enter/Space) opens the picker, dragging a file onto it
// works too, and the chosen file's name stays visible. onFile(fileOrNull)
// fires exactly like the old input's onChange did.
export function FileDrop({ accept, file, onFile, hint, style }) {
  const inputRef = React.useRef(null);
  const [over, setOver] = React.useState(false);
  return (
    <div
      className={"ui-filedrop" + (over ? " ui-filedrop--over" : "")}
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      style={style}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
      {file ? (
        <>
          <div className="ui-filedrop__name">{file.name}</div>
          <div className="ui-filedrop__hint">Tap to choose a different file</div>
        </>
      ) : (
        <>
          <div className="ui-filedrop__name">Choose a file, or drag it here</div>
          {hint && <div className="ui-filedrop__hint">{hint}</div>}
        </>
      )}
    </div>
  );
}

// ---- Page ----------------------------------------------------------------
// The shared page container: every routed page sits on the same grid, with
// the same gutters, at one of three deliberate widths — "narrow" (720, focused
// forms like the office uploads), default (960), or "wide" (1400, the big
// grids: dispatch, roster, training opportunities). Full-bleed backgrounds
// stay on the page's own outer div; Page only owns width and gutters.
export function Page({ narrow, wide, style, children, ...props }) {
  const cls = "ui-page" + (narrow ? " ui-page--narrow" : "") + (wide ? " ui-page--wide" : "");
  return (
    <div className={cls} style={style} {...props}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, style }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14, textAlign: "left", ...style }}>
      <div style={{ minWidth: 220, flex: "1 1 260px" }}>
        <h1 className="ui-title" style={{ margin: 0, color: theme.colors.primary, fontSize: T.text.xxl, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {title}
        </h1>
        {/* the one deliberate brand flourish (2026-07-15): a short gold rule
            under every page title — gold appears here and nowhere else */}
        <div aria-hidden="true" style={{ width: 44, height: 3, borderRadius: 2, background: "var(--gold)", margin: "7px 0 0" }} />
        {subtitle && (
          <p style={{ margin: "6px 0 0", color: T.textMuted, fontSize: T.text.sm, lineHeight: 1.5 }}>{subtitle}</p>
        )}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{actions}</div>}
    </div>
  );
}

// ---- Segmented ---------------------------------------------------------------
// options: [{value, label}] — the pill view-switcher. Scrolls sideways when
// it doesn't fit (phones) instead of truncating.
// When the strip overflows, the cut-off edge fades out so it reads as "more
// this way" instead of a truncated label (Paul Merrill, iPhone 16 Pro Max,
// 2026-09-06: "Vessel Calen" looked like the app was not fitting his screen).
export function Segmented({ options, value, onChange, style }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [options]);
  const cls = ["ui-seg", edges.left && "ui-seg--more-left", edges.right && "ui-seg--more-right"].filter(Boolean).join(" ");
  return (
    <div ref={ref} className={cls} role="tablist" style={style}>
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          data-active={value === o.value ? "true" : "false"}
          className="ui-seg__btn"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- Chip --------------------------------------------------------------------
// Small status tag: <Chip tone="warn">TRAVEL</Chip>
export function Chip({ tone = "neutral", style, children }) {
  const tones = {
    neutral: { bg: T.surfaceMuted, fg: T.textMuted, border: T.border },
    info: { bg: "#EAF1F8", fg: theme.colors.primary, border: "#C9DCEE" },
    success: T.success,
    warn: T.warn,
    danger: T.danger,
  };
  const c = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: "inline-block", fontSize: T.text.xs, fontWeight: 800, letterSpacing: "0.05em",
      textTransform: "uppercase", color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: T.radius.sm, padding: "2px 8px", ...style,
    }}>
      {children}
    </span>
  );
}

// ---- Note --------------------------------------------------------------------
// Inline callout banner: <Note tone="warn">You're offline…</Note>
export function Note({ tone = "warn", style, children }) {
  // info was missing and fell back to amber (fixed 2026-08-21: tone="info"
  // banners now render calm blue like the info Chip).
  const c = { warn: T.warn, danger: T.danger, success: T.success, info: T.info }[tone] || T.warn;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.fg,
      borderRadius: T.radius.md, padding: "10px 14px", fontSize: T.text.sm, lineHeight: 1.5, ...style,
    }}>
      {children}
    </div>
  );
}

// ---- AutoGrowText ------------------------------------------------------------
// A text field that grows to fit its content instead of clipping — for free-text
// entries (detention descriptions, notes) that can run long. Pairs with the
// .ui-autogrow CSS (a hidden sizing twin); browser-agnostic, no `field-sizing`
// dependency, so it behaves the same on iOS Safari, Chrome/Android, and older
// engines. onChange receives (value, event); Enter is swallowed so it stays a
// one-line-in-spirit field (newlines are stripped).
export function AutoGrowText({
  value = "", onChange, onBlur, onFocus, placeholder = "", disabled = false,
  highlight = false, wrapClassName = "", style, textareaStyle,
}) {
  return (
    <div
      className={`ui-autogrow${highlight ? " is-highlight" : ""}${wrapClassName ? " " + wrapClassName : ""}`}
      data-replicated-value={value || placeholder || " "}
      style={style}
    >
      <textarea
        rows={1}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value.replace(/\n/g, " "), e)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) e.preventDefault(); }}
        onBlur={onBlur}
        onFocus={onFocus}
        style={textareaStyle}
      />
    </div>
  );
}

// ---- Skeleton ----------------------------------------------------------------
// A gray shimmering placeholder block. Compose these to mirror a page's layout
// while its data loads. `w`/`h` accept numbers (px) or CSS strings.
export function Skeleton({ w = "100%", h = 14, r, style }) {
  return (
    <span
      className="ui-skeleton"
      aria-hidden="true"
      style={{
        display: "block",
        width: typeof w === "number" ? `${w}px` : w,
        height: typeof h === "number" ? `${h}px` : h,
        borderRadius: r != null ? r : T.radius.sm,
        ...style,
      }}
    />
  );
}

// ---- SkeletonText ------------------------------------------------------------
// N stacked lines; the last is shortened so it reads like a paragraph.
export function SkeletonText({ lines = 3, style }) {
  return (
    <div style={{ display: "grid", gap: 8, ...style }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} w={i === lines - 1 ? "60%" : "100%"} h={12} />
      ))}
    </div>
  );
}

// ---- SkeletonCard ------------------------------------------------------------
// A card-shaped placeholder (title bar + body lines). Matches Card geometry.
export function SkeletonCard({ lines = 3, style }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius.lg,
      boxShadow: T.shadowCard, padding: "16px 18px", ...style,
    }}>
      <Skeleton w="45%" h={16} style={{ marginBottom: 12 }} />
      <SkeletonText lines={lines} />
    </div>
  );
}

// ---- LoadingState ------------------------------------------------------------
// The standard "we're fetching" screen — replaces ad-hoc "Loading..." text.
// On ship wifi a request can stall silently (navigator.onLine lies, see the
// offline-PWA work), so a placeholder that pulses forever reads as broken.
// After `timeout` ms we stop implying content is coming and offer a way out.
//   children — optional skeleton layout shown while loading (preferred on
//              high-traffic pages); when omitted, a centered spinner+label.
//   label    — the centered message (default "Loading…")
//   timeout  — ms before the stalled-connection fallback (default 8000; pass
//              null to disable, e.g. for deliberately long operations)
//   onRetry  — called by the Try again button; defaults to a full reload
export function LoadingState({ children, label = "Loading…", timeout = 8000, onRetry, style }) {
  const [stalled, setStalled] = React.useState(false);
  React.useEffect(() => {
    if (timeout == null) return undefined;
    const id = setTimeout(() => setStalled(true), timeout);
    return () => clearTimeout(id);
  }, [timeout]);

  if (stalled) {
    return (
      <div className="ui-loading" style={style} role="status" aria-live="polite">
        <div className="ui-loading__stall">
          <p style={{ margin: 0, fontWeight: 700, color: T.ink }}>Still loading…</p>
          <p style={{ margin: "4px 0 14px", color: T.textMuted, fontSize: T.text.sm, lineHeight: 1.5 }}>
            This is taking longer than usual. Check your connection, then try again.
          </p>
          <Button variant="outline" size="sm" onClick={onRetry || (() => window.location.reload())}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (children) return children;

  return (
    <div className="ui-loading" style={style} role="status" aria-live="polite">
      <span className="ui-spinner" aria-hidden="true" />
      <span style={{ color: T.textMuted, fontSize: T.text.sm }}>{label}</span>
    </div>
  );
}

